import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Users, Loader2, AlertTriangle, CheckCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchInviteInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/invite/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setInviteInfo(data);
        if (data.existing_user_name) {
          setFullName(data.existing_user_name);
        }
      } else if (response.status === 404) {
        setError('This invitation link is invalid or has already been used.');
      } else if (response.status === 410) {
        setError('This invitation has expired. Please ask for a new invitation.');
      } else {
        setError('Failed to load invitation details.');
      }
    } catch (err) {
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInviteInfo();
  }, [fetchInviteInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteInfo?.is_existing_user) {
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/team/invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: password,
          full_name: fullName
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Welcome to ${data.team_name}!`);
        
        // Auto-login the user with the token
        if (data.access_token) {
          await loginWithToken(data.access_token, {
            email: inviteInfo.email,
            full_name: fullName || inviteInfo.email.split('@')[0]
          });
          navigate('/dashboard');
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      toast.error('Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-100 p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">You're Invited!</h2>
            <p className="text-slate-600">
              <strong>{inviteInfo?.inviter_name}</strong> invited you to join <strong>{inviteInfo?.team_name}</strong>
            </p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-slate-600 mb-1">Your role</p>
            <Badge className="bg-indigo-600 text-white text-base px-4 py-1">
              {inviteInfo?.role?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </Badge>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteInfo?.email || ''}
                disabled
                className="bg-slate-50"
              />
            </div>
            
            {!inviteInfo?.is_existing_user && (
              <>
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="invite-full-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Create Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      data-testid="invite-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    data-testid="invite-confirm-password"
                  />
                </div>
              </>
            )}
            
            {inviteInfo?.is_existing_user && (
              <div className="bg-green-50 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Account Found</p>
                  <p className="text-sm text-green-700">
                    You already have a Formlio account. Click below to join the team.
                  </p>
                </div>
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={submitting}
              data-testid="accept-invite-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {inviteInfo?.is_existing_user ? 'Join Team' : 'Create Account & Join'}
            </Button>
          </form>
          
          <p className="text-xs text-slate-500 text-center mt-6">
            By accepting, you agree to our Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
